import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ChainBadge } from '../components/common/chain-badge';
import { DecimalDisplay } from '../components/common/decimal-display';
import { DateDisplay } from '../components/common/date-display';
import { EmptyState } from '../components/common/empty-state';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useAssetLedger, useAssets, useChains } from '../lib/api/hooks/use-queries';
import { useEnrichAsset, useReviewAssetsAccounting } from '../lib/api/hooks/use-mutations';
import type { ReviewableAccountingTreatment } from '../lib/api/hooks/mutations/misc';
import { resolveChain } from '../lib/chains';
import { toast } from '../components/ui/use-toast';
import { AccountingTreatmentDialog } from '../components/assets/accounting-treatment-dialog';
import { PeggedPriceCard } from '../components/assets/pegged-price-card';

function AssetDetail(): JSX.Element {
  const { assetId } = Route.useParams();
  const id = Number(assetId);
  const assets = useAssets();
  const chains = useChains();
  const enrich = useEnrichAsset();
  const reviewAccounting = useReviewAssetsAccounting();
  const ledger = useAssetLedger(id);
  const [reviewTreatment, setReviewTreatment] = useState<ReviewableAccountingTreatment | null>(
    null,
  );

  if (assets.isLoading) return <Skeleton className="h-48" />;

  const asset = assets.data?.find((a) => a.id === id);
  if (!asset) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Asset introuvable</CardTitle>
        </CardHeader>
        <CardContent>
          <Link to="/positions" className="text-sm underline">
            Retour aux positions
          </Link>
        </CardContent>
      </Card>
    );
  }

  const { name: chainName, slug: chainSlug } = resolveChain(asset.chainId, chains.data);
  const accountingTarget =
    asset.accountingAssetId !== null && asset.accountingAssetId !== asset.id
      ? assets.data?.find((candidate) => candidate.id === asset.accountingAssetId)
      : undefined;
  const canonicalTargets = (assets.data ?? [])
    .filter(
      (candidate) =>
        candidate.id !== asset.id &&
        candidate.accountingTreatment === 'HELD_522' &&
        (candidate.accountingAssetId === null || candidate.accountingAssetId === candidate.id),
    )
    .map((candidate) => ({ id: candidate.id, symbol: candidate.symbol, name: candidate.name }));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            {asset.symbol}
            <ChainBadge slug={chainSlug} name={chainName} />
            {asset.needsReview ? <Badge variant="warning">À enrichir</Badge> : null}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <span className="text-muted-foreground">Nom: </span>
            {asset.name}
          </div>
          <div>
            <span className="text-muted-foreground">Contrat: </span>
            <span className="font-mono">{asset.contractAddress ?? 'natif'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Décimales: </span>
            {asset.decimals}
          </div>
          <div>
            <span className="text-muted-foreground">CoinGecko ID: </span>
            <span className="font-mono">{asset.coingeckoId ?? '—'}</span>
          </div>
          {asset.needsReview ? (
            <Button
              size="sm"
              onClick={() =>
                enrich.mutate(asset.id, {
                  onSuccess: () => toast({ title: 'Asset enrichi' }),
                  onError: (err) =>
                    toast({ title: 'Erreur', description: err.message, variant: 'destructive' }),
                })
              }
            >
              Enrichir via CoinGecko
            </Button>
          ) : null}
          <div className="space-y-2 rounded-md border p-3">
            <p className="font-medium">Traitement comptable</p>
            <div>
              {asset.accountingTreatment === 'HELD_522' ? (
                <Badge variant="success">Validé en 522 — jetons détenus</Badge>
              ) : asset.accountingTreatment === 'REQUIRES_EC' ? (
                <Badge variant="destructive">À qualifier avec l’expert-comptable</Badge>
              ) : (
                <Badge variant="warning">Traitement comptable non validé</Badge>
              )}
            </div>
            {asset.accountingReviewReason ? (
              <p className="text-xs text-muted-foreground">
                Motif : {asset.accountingReviewReason}
              </p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              {accountingTarget
                ? `Actif comptable commun : ${accountingTarget.symbol} — ${accountingTarget.name}`
                : 'Actif comptable : cet actif reste une cible canonique distincte.'}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setReviewTreatment('HELD_522')}>
                Valider en 522 — jetons détenus
              </Button>
              <Button size="sm" variant="outline" onClick={() => setReviewTreatment('REQUIRES_EC')}>
                À qualifier avec l’expert-comptable
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <PeggedPriceCard
        key={`${asset.id}:${asset.peggedPriceEur ?? 'market'}`}
        assetId={asset.id}
        symbol={asset.symbol}
        peggedPriceEur={asset.peggedPriceEur}
      />
      <AccountingTreatmentDialog
        open={reviewTreatment !== null}
        assetCount={1}
        initialTreatment={reviewTreatment ?? 'HELD_522'}
        assetId={asset.id}
        initialAccountingAssetId={asset.accountingAssetId}
        canonicalTargets={canonicalTargets}
        submitting={reviewAccounting.isPending}
        onOpenChange={(open) => {
          if (!open) setReviewTreatment(null);
        }}
        onSubmit={(decision) =>
          reviewAccounting.mutate(
            { assetIds: [asset.id], ...decision },
            {
              onSuccess: (outcome) => {
                if (!outcome.succeededAssetIds.includes(asset.id)) {
                  toast({
                    title: 'Traitement comptable non enregistré',
                    description: `La décision pour ${asset.symbol} n’a pas été enregistrée. Vous pouvez réessayer.`,
                    variant: 'destructive',
                  });
                  return;
                }
                toast({ title: 'Traitement comptable enregistré' });
                setReviewTreatment(null);
              },
            },
          )
        }
      />
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Historique CUMP (coût unitaire moyen pondéré)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {ledger.isLoading ? (
            <Skeleton className="h-32" />
          ) : ledger.data?.pricesMissing ? (
            <EmptyState
              title="Historique indisponible"
              description="Prix manquants pour cet actif — enrichissez-le pour reconstruire le CUMP."
            />
          ) : (ledger.data?.rows.length ?? 0) === 0 ? (
            <EmptyState
              title="Aucun mouvement"
              description="Aucun mouvement classé pour cet actif."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Sens</TableHead>
                  <TableHead className="text-right">Quantité</TableHead>
                  <TableHead className="text-right">Prix unitaire</TableHead>
                  <TableHead className="text-right">Qté cumulée</TableHead>
                  <TableHead className="text-right">CUMP</TableHead>
                  <TableHead className="text-right">P&amp;L réalisé</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(ledger.data?.rows ?? []).map((r) => (
                  <TableRow key={r.sequenceNum}>
                    <TableCell className="whitespace-nowrap">
                      <DateDisplay value={r.occurredAt} withTime />
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.movement === 'IN' ? 'success' : 'outline'}>
                        {r.movement === 'IN' ? 'Entrée' : 'Sortie'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DecimalDisplay value={r.qty} mode="quantity" />
                    </TableCell>
                    <TableCell className="text-right">
                      <DecimalDisplay value={r.unitPriceEur} mode="eur" />
                    </TableCell>
                    <TableCell className="text-right">
                      <DecimalDisplay value={r.runningQty} mode="quantity" />
                    </TableCell>
                    <TableCell className="text-right">
                      <DecimalDisplay value={r.runningAvgCostEur} mode="eur" />
                    </TableCell>
                    <TableCell className="text-right">
                      {r.movement === 'OUT' ? (
                        <DecimalDisplay value={r.realizedPlEur} mode="eur" colored />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/assets/$assetId')({ component: AssetDetail });
